import { TableColumnDirective } from '../directives/table-column.directive';
import { TableState } from './table-state';
import {
  Filter,
  Filterable,
  FilterDelegate,
  MatchValues,
  SearchType,
  TableStateFilterEvent,
} from './types';

export class TableFilter<T extends Record<string, unknown>>
  implements Filterable<T>
{
  constructor(private readonly filters: Filter<T>[]) {}

  public static from<T extends Record<string, unknown>>(
    state: TableState
  ): Filterable<T> {
    return new TableFilter<T>(state.filter);
  }

  public allow(element: T): boolean {
    const allow = this.filters.reduce((acc, filter) => {
      if (!acc) {
        return acc;
      }

      if (!filter.apply()) {
        return acc;
      }

      return filter.allow(element);
    }, true);

    return allow;
  }
}

export class FilterValue {
  constructor(private readonly element: Record<string, unknown>) {}

  public cast(key: string): string {
    const value = this.element[key];

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number') {
      return value.toString();
    }

    return `${value}`;
  }
}

export class EmptyFilter<T = unknown> implements Filter<T> {
  public allow(_: T): boolean {
    return true;
  }

  public match(_: string): boolean {
    return false;
  }

  public apply(): boolean {
    return true;
  }

  public values(): MatchValues {
    return {
      field: 'empty',
      type: 'none',
      values: [],
    };
  }
}

export class StrictFilter<T extends Record<string, unknown>>
  implements Filter<T>
{
  constructor(
    private readonly _key: string,
    private readonly _values: string[]
  ) {}

  public allow(element: T): boolean {
    return this._values.some(
      (value) => value === new FilterValue(element).cast(this._key)
    );
  }

  public match(field: string): boolean {
    return this._key === field;
  }

  public apply(): boolean {
    return this._values.length > 0;
  }

  public values(): MatchValues {
    return {
      field: this._key,
      type: 'option',
      values: this._values,
    };
  }
}

export class PartialFilter<T extends Record<string, unknown>>
  implements Filter<T>
{
  private readonly _regex: RegExp;

  constructor(
    private readonly _key: string,
    private readonly _value: string,
    private readonly type: SearchType = 'start'
  ) {
    this._regex = new RegExp(`^${_value}`, 'i');
  }

  public allow(element: T): boolean {
    const value = new FilterValue(element).cast(this._key);

    if (this.type === 'contains') {
      return value.toLowerCase().includes(this._value.toLowerCase());
    }

    return this._regex.test(value);
  }

  public match(field: string): boolean {
    return this._key === field;
  }

  public apply(): boolean {
    return this._value.length > 0;
  }

  public values(): MatchValues {
    return {
      field: this._key,
      type: 'search',
      values: [this._value],
    };
  }
}

export class CustomFilter<T extends Record<string, unknown>>
  implements Filter<T>
{
  constructor(
    private readonly _field: string,
    private readonly _values: string[],
    private readonly _delegate: FilterDelegate<T>
  ) {}

  public apply(): boolean {
    return this._values.length > 0;
  }

  public allow(element: T): boolean {
    const filter = this._delegate.filter(this._field, this._values);
    return filter.allow(element);
  }

  public match(field: string): boolean {
    return this._field === field;
  }

  public values(): MatchValues {
    return {
      field: this._field,
      type: 'custom',
      values: this._values,
    };
  }
}

export class TableFilterFactory {
  constructor(
    private readonly _filters: TableStateFilterEvent[],
    private readonly _columns: TableColumnDirective[]
  ) {}

  public create(): Filter<Record<string, unknown>>[] {
    return this._filters.map((filter) => {
      const column = this._columns.find((c) => c.field === filter.field);

      if (!column) {
        return new StrictFilter(filter.field, filter.values);
      }

      return this._filter(filter, column);
    });
  }

  private _filter(
    filter: TableStateFilterEvent,
    column: TableColumnDirective
  ): Filter<Record<string, unknown>> {
    switch (filter.type) {
      case 'option':
        return new StrictFilter(filter.field, filter.values);
      case 'search':
        return new PartialFilter(
          filter.field,
          filter.values[0],
          column.searchType
        );
      case 'custom':
        return new CustomFilter(filter.field, filter.values, column.delegate!);
      default:
        return new EmptyFilter();
    }
  }
}
